<template>
  <mobile-responsive-shell :title="title">
    <div class="stk-mobile">

      <div class="stk-mobile-toolbar">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="refresh">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="print">
          <i class="bi bi-printer"></i> Imprimer
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :class="{ active: showFilters }"
          @click="showFilters = !showFilters"
        >
          <i class="bi bi-funnel"></i> Filtres
        </button>
      </div>

      <div v-show="showFilters" class="stk-mobile-filters card card-body py-3 mb-3">
        <div class="mb-2">
          <label class="form-label small mb-0">Catégorie</label>
          <select v-model="filter.section_reference" class="form-select form-select-sm" @change="onSectionChange">
            <option value="">—</option>
            <option v-for="s in sections" :key="s.reference" :value="s.reference">
              {{ s.fullName }}
            </option>
          </select>
        </div>
        <div class="mb-2">
          <label class="form-label small mb-0">Produit</label>
          <select v-model="filter.item_reference" class="form-select form-select-sm">
            <option value="">—</option>
            <option v-for="it in filteredItems" :key="it.reference" :value="it.reference">
              {{ it.fullName }}
            </option>
          </select>
        </div>
        <div class="mb-2">
          <label class="form-label small mb-0">Stock</label>
          <select v-model="filter.stock_reference" class="form-select form-select-sm">
            <option value="">—</option>
            <option v-for="s in stocks" :key="s.reference" :value="s.reference">
              {{ s.fullName }}
            </option>
          </select>
        </div>
        <div class="mb-2">
          <label class="form-label small mb-0">Dépôt</label>
          <select v-model="filter.deposit_reference" class="form-select form-select-sm">
            <option value="">—</option>
            <option v-for="d in deposits" :key="d.reference" :value="d.reference">
              {{ d.fullName }}
            </option>
          </select>
        </div>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-primary btn-sm flex-fill" @click="applyFilter">
            Appliquer
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm flex-fill" @click="resetFilter">
            Réinit.
          </button>
        </div>
      </div>

      <p v-if="!stockItems || !stockItems.length" class="text-muted text-center py-4">
        Aucun article en stock.
      </p>

      <div v-else class="stk-mobile-list">
        <article
          v-for="(row, idx) in stockItems"
          :key="row.reference || idx"
          class="stk-mobile-card"
        >
          <header class="stk-mobile-card-head">
            <div class="stk-mobile-title">
              <span class="stk-ref">{{ row.item_reference }}</span>
              <span class="stk-name">{{ row.itemFullName }}</span>
            </div>
            <div v-if="userRole === 'Admin'" class="d-flex gap-1">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                @click="$router.push({ name: 'stocks-edit', params: { reference: row.reference } })"
              >
                <i class="bi bi-pencil-square"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                @click="$router.push({ name: 'stocks-delete', params: { reference: row.reference } })"
              >
                <i class="bi bi-file-x-fill"></i>
              </button>
            </div>
          </header>

          <div class="stk-mobile-meta">
            <span v-if="row.sectionFullName">
              <i class="bi bi-tag text-secondary"></i> {{ row.sectionFullName }}
            </span>
            <span v-if="row.stockFullName || row.depositFullName">
              <i class="bi bi-box-seam text-secondary"></i>
              {{ row.stockFullName }}<span v-if="row.depositFullName"> / {{ row.depositFullName }}</span>
            </span>
          </div>

          <div class="stk-mobile-stats">
            <div>
              <span class="stk-lbl">Quantité</span>
              <span
                class="stk-qty-badge"
                :class="qtyClass(row.quantity)"
              >
                {{ row.quantity }} {{ row.measuring }}
              </span>
            </div>
            <div v-if="userRole === 'Admin'">
              <span class="stk-lbl">Prix d'achat</span>
              <span class="text-primary fw-semibold">{{ fmt(row.purchasePriceTTC) }}</span>
            </div>
            <div v-if="row.cold_room">
              <span class="stk-lbl">Chambre froide</span>
              <span>{{ row.cold_room.number }}</span>
            </div>
          </div>
        </article>
      </div>

      <div v-if="stockItems && stockItems.length" class="stk-mobile-summary">
        <div>Articles <strong>{{ stockItems.length }}</strong></div>
        <div>Qté Σ <strong>{{ totalQty }}</strong></div>
      </div>

    </div>
  </mobile-responsive-shell>
</template>

<script>
import { mapGetters } from "vuex";
import MobileResponsiveShell from "../components/MobileResponsiveShell.vue";

export default {
  name: "MobileStocksListing",
  components: { MobileResponsiveShell },
  data() {
    return {
      title: "État de stock",
      showFilters: false,
      filter: {
        section_reference: "",
        item_reference: "",
        stock_reference: "",
        deposit_reference: "",
      },
    };
  },
  computed: {
    ...mapGetters("stock", {
      stocks: "getStocks",
      stockItems: "getStockItems",
    }),
    ...mapGetters("section", { sections: "getAll" }),
    ...mapGetters("item", { items: "getAll" }),
    ...mapGetters("deposit", { deposits: "getAll" }),
    ...mapGetters("auth", { userRole: "getUserRole" }),
    filteredItems() {
      if (!this.filter.section_reference) return this.items;
      return this.items.filter(i => i.section_reference === this.filter.section_reference);
    },
    totalQty() {
      if (!this.stockItems) return 0;
      return this.stockItems.reduce((a, r) => a + parseFloat(r.quantity || 0), 0);
    },
  },
  mounted() {
    this.$store.dispatch("stock/getAll");
    this.$store.dispatch("stock/fetchStocksItems");
    this.$store.dispatch("section/getAll");
    this.$store.dispatch("item/getAll");
    this.$store.dispatch("deposit/getAll");
  },
  methods: {
    fmt(v) {
      const n = parseFloat(v);
      if (Number.isNaN(n)) return "—";
      return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    qtyClass(qty) {
      const q = parseFloat(qty || 0);
      if (q === 0) return "stk-qty--danger";
      if (q <= 5) return "stk-qty--warning";
      return "stk-qty--ok";
    },
    onSectionChange() {
      this.filter.item_reference = "";
    },
    async refresh() {
      await Promise.all([
        this.$store.dispatch("stock/getAll"),
        this.$store.dispatch("stock/fetchStocksItems"),
        this.$store.dispatch("section/getAll"),
        this.$store.dispatch("item/getAll"),
        this.$store.dispatch("deposit/getAll"),
      ]);
      this.resetFilter();
    },
    async applyFilter() {
      await this.$store.dispatch("stock/filter", { ...this.filter });
    },
    async resetFilter() {
      this.filter = { section_reference: "", item_reference: "", stock_reference: "", deposit_reference: "" };
      await this.$store.dispatch("stock/fetchStocksItems");
    },
    async print() {
      await this.$store.dispatch("stock/print", { ...this.filter });
    },
  },
};
</script>

<style scoped>
.stk-mobile-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.stk-mobile-filters {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.stk-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.stk-mobile-card {
  background: #fff;
  border-radius: 12px;
  padding: 0.875rem 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.stk-mobile-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}
.stk-mobile-title {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1;
  min-width: 0;
}
.stk-ref {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 600;
}
.stk-name {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.25;
  word-break: break-word;
}
.stk-mobile-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1rem;
  font-size: 1rem;
  color: #495057;
  margin-bottom: 0.6rem;
}
.stk-mobile-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.4rem 0.75rem;
  font-size: 1rem;
}
.stk-lbl {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c757d;
  margin-bottom: 0.1rem;
}
.stk-qty-badge {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 1rem;
}
.stk-qty--danger {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}
.stk-qty--warning {
  background: rgba(255, 193, 7, 0.2);
  color: #856404;
}
.stk-qty--ok {
  background: rgba(25, 135, 84, 0.1);
  color: #198754;
}
.stk-mobile-summary {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-radius: 10px;
  font-size: 1.05rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}
</style>
